import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';
import ModelLoader from '@/utils/ModelLoader';
import {
    installCullShader,
    setMaterialTrimRadius,
    setMaterialDecimation,
    attachCullDistances,
    attachDecimationKeys,
    buildTrimDistribution,
    visibleAtRadius,
    createCameraPath,
} from '@/lib/viewer/pointCloudCull';

const BASE_POINT_SIZE = 0.02;
const SELECTED_POINT_SIZE = 0.03;   // subtle emphasis on the selected scan

/**
 * Framework-agnostic three.js engine for rendering one or more scan point clouds, each as its own
 * transformable group keyed by scanId. Knows nothing about Vue, markers, or model-version admin —
 * Vue components instantiate it and drive it (composition, not inheritance).
 *
 * Phase A scope: scene/camera/renderer/controls/render-loop + a scan registry that loads PLYs into
 * positioned groups. Selection + a transform gizmo + save build on top of this in later phases
 * (setScanTransform / getScanTransform are already provided for that).
 *
 * Scene configuration mirrors the runtime ModelViewer so scans look identical in both.
 */
export default class ViewerEngine {
    constructor() {
        // Accelerated raycasting for point-cloud picking (used from Phase B onward). Setting the
        // prototype extensions is idempotent and cheap.
        THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
        THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
        THREE.Mesh.prototype.raycast = acceleratedRaycast;

        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.orbitControls = null;
        this.transformControls = null;
        this.container = null;
        this.resizeObserver = null;
        this.animationFrameId = null;

        // scanId -> { group, points }
        this.scans = new Map();

        // The scan currently attached to the transform gizmo, and a callback fired while it moves.
        this.selectedScanId = null;
        this.onChange = null;

        // Click-to-select: raycaster + callback fired with the clicked scanId.
        this.raycaster = new THREE.Raycaster();
        this.raycaster.params.Points.threshold = 0.05;
        this.onScanClick = null;
        this.pointerDownAt = null;

        // Dimming of non-selected scans (toggleable + adjustable from the UI).
        this.dimEnabled = true;
        this.dimOpacity = 0.35;

        // fetch/cache/progress only — no scene side effects, so the scene/camera args are unused.
        this.modelLoader = new ModelLoader(null, null);
    }

    /**
     * Builds the scene into the given container element and starts the render loop.
     */
    mount(container) {
        this.container = container;
        const width = container.clientWidth;
        const height = container.clientHeight;

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x1a1f24);

        this.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 500);
        this.camera.up.set(0, 0, 1);
        this.camera.position.set(3, 3, 3);
        this.camera.updateProjectionMatrix();

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(width, height);
        container.appendChild(this.renderer.domElement);

        this.scene.add(new THREE.AmbientLight(0xffffff, 40.5));

        this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
        this.orbitControls.enableDamping = true;
        this.orbitControls.dampingFactor = 0.14;
        this.orbitControls.minDistance = 0.5;
        // Generous zoom-out for the alignment editor (scans can sit far apart); frameBox raises it
        // further for large compositions.
        this.orbitControls.maxDistance = 2000;
        this.orbitControls.mouseButtons = {
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.PAN,
            RIGHT: THREE.MOUSE.DOLLY,
        };

        // Transform gizmo for moving a selected scan. Disables orbit while dragging and reports
        // changes so the caller can flag unsaved edits.
        this.transformControls = new TransformControls(this.camera, this.renderer.domElement);
        this.transformControls.setMode('translate');
        this.transformControls.setSize(1.1);
        this.transformControls.addEventListener('dragging-changed', (e) => {
            this.orbitControls.enabled = !e.value;
        });
        this.transformControls.addEventListener('objectChange', () => {
            if (this.onChange) this.onChange(this.selectedScanId);
        });
        this.scene.add(this.transformControls.getHelper());

        // Click-to-select: distinguish a click from an orbit-drag / gizmo-drag.
        this.handlePointerDown = (e) => { this.pointerDownAt = { x: e.clientX, y: e.clientY }; };
        this.handlePointerUp = (e) => {
            const down = this.pointerDownAt;
            this.pointerDownAt = null;
            if (!down) return;
            const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y) > 5;
            if (moved || this.transformControls.dragging) return;
            this.pickScanAt(e);
        };
        this.renderer.domElement.addEventListener('pointerdown', this.handlePointerDown);
        this.renderer.domElement.addEventListener('pointerup', this.handlePointerUp);

        this.resizeObserver = new ResizeObserver(() => this.onResize());
        this.resizeObserver.observe(container);

        this.startAnimationLoop();
    }

    /** Registers a callback fired (with the scanId) when a scan's point cloud is clicked. */
    setOnScanClick(callback) {
        this.onScanClick = callback;
    }

    /** Raycasts the visible point clouds at a pointer event and reports the clicked scan. */
    pickScanAt(event) {
        if (!this.onScanClick) return;

        const rect = this.renderer.domElement.getBoundingClientRect();
        const ndc = new THREE.Vector2(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1,
        );
        this.raycaster.setFromCamera(ndc, this.camera);

        const points = [...this.scans.values()]
            .filter(e => e.group.visible !== false)
            .map(e => e.points);

        const hits = this.raycaster.intersectObjects(points, false);
        if (hits.length) this.onScanClick(hits[0].object.userData.scanId);
    }

    /** Shows or hides a scan. */
    setScanVisible(scanId, visible) {
        const entry = this.scans.get(scanId);
        if (entry) entry.group.visible = visible;
    }

    /** Registers a callback fired (with the scanId) whenever the selected scan is transformed. */
    setOnChange(callback) {
        this.onChange = callback;
    }

    /**
     * Selects a scan: highlights it (and dims the rest) and, when attachGizmo is true, attaches the
     * move gizmo. Pass attachGizmo=false for the primary base — it gets the selection emphasis for
     * its scan-specific actions but stays fixed (no movable gizmo).
     */
    selectScan(scanId, attachGizmo = true) {
        const entry = this.scans.get(scanId);
        if (!entry) return;
        this.selectedScanId = scanId;
        if (attachGizmo) this.transformControls.attach(entry.group);
        else this.transformControls?.detach();
        this.applySelectionStyle();
    }

    /** Detaches the transform gizmo and clears the highlight. */
    deselectScan() {
        this.selectedScanId = null;
        this.transformControls?.detach();
        this.applySelectionStyle();
    }

    /** Sets the gizmo mode: 'translate' | 'rotate' | 'scale'. */
    setGizmoMode(mode) {
        this.transformControls?.setMode(mode);
    }

    /** Enables/disables grid + angle snapping on the gizmo. */
    setSnapping(enabled) {
        if (!this.transformControls) return;
        this.transformControls.setTranslationSnap(enabled ? 0.1 : null);
        this.transformControls.setRotationSnap(enabled ? THREE.MathUtils.degToRad(15) : null);
        this.transformControls.setScaleSnap(enabled ? 0.1 : null);
    }

    /** Enables/disables dimming of the non-selected scans. */
    setDimEnabled(enabled) {
        this.dimEnabled = enabled;
        this.applySelectionStyle();
    }

    /** Sets how visible the non-selected scans are while one is selected (0..1). */
    setDimOpacity(opacity) {
        this.dimOpacity = opacity;
        this.applySelectionStyle();
    }

    /**
     * Emphasises the selected scan (larger points, full opacity) and — when dimming is enabled —
     * fades the rest to dimOpacity. No-op styling when nothing is selected.
     */
    applySelectionStyle() {
        const hasSelection = this.selectedScanId != null;
        for (const [scanId, entry] of this.scans) {
            const material = entry.points.material;
            const isSelected = scanId === this.selectedScanId;

            if (!hasSelection || isSelected) {
                material.opacity = 1;
                material.transparent = false;
                material.size = isSelected ? SELECTED_POINT_SIZE : BASE_POINT_SIZE;
            } else if (this.dimEnabled) {
                material.opacity = this.dimOpacity;
                material.transparent = true;
                material.size = BASE_POINT_SIZE;
            } else {
                material.opacity = 1;
                material.transparent = false;
                material.size = BASE_POINT_SIZE;
            }
            material.needsUpdate = true;
        }
    }

    /**
     * Loads a scan's PLY into a positioned group and registers it by scanId. If the scan is already
     * loaded it is replaced. onPhase(phase, value) reports 'download' (0..1|null) / 'processing',
     * matching ModelLoader's contract so callers can drive a progress UI.
     *
     * cullCenters (optional): an array of {x,y,z} in scan-LOCAL coordinates (typically the scan's
     * camera-helper positions). When provided, the engine precomputes a per-vertex "distance to
     * nearest centre" attribute used by setScanTrimRadius for live shader-side culling.
     */
    async loadScan({ scanId, modelPath, transform, cullCenters, pathColor }, onPhase) {
        if (!scanId || !modelPath) return null;

        const arrayBuffer = await this.modelLoader.fetchModelWithCache(modelPath, onPhase);

        // Yield once so the UI can paint the "processing" state before the synchronous parse.
        if (onPhase) onPhase('processing');
        await new Promise((resolve) => setTimeout(resolve, 0));

        const geometry = new PLYLoader().parse(arrayBuffer);

        let trimDistribution = null;
        if (cullCenters?.length) {
            await attachCullDistances(geometry, cullCenters);
            trimDistribution = buildTrimDistribution(geometry);
        }
        // Per-vertex random keys so the decimation slider can thin to any density live (no cameras needed).
        attachDecimationKeys(geometry);

        const material = installCullShader(
            new THREE.PointsMaterial({ size: BASE_POINT_SIZE, vertexColors: true }),
        );
        const points = new THREE.Points(geometry, material);
        points.userData.scanId = scanId;  // so click-picking can map a hit back to its scan

        const group = new THREE.Group();
        group.name = `scan:${scanId}`;
        group.add(points);

        // Path-line through the camera helpers (rendering the spine of the tunnel). Lives inside
        // the scan group so the scan's transform carries it. Caller order defines line order, so
        // editors should sort centres by capture sequence (e.g. imageName) before passing them.
        const cameraPath = createCameraPath(cullCenters, pathColor ? { color: pathColor } : {});
        if (cameraPath) {
            this.sizeCameraPath(cameraPath);  // fat-line pixel width needs the live viewport size
            group.add(cameraPath);
        }

        this.applyTransform(group, transform);

        this.removeScan(scanId);
        this.scene.add(group);
        this.scans.set(scanId, { group, points, cameraPath, trimDistribution });

        // Keep dimming consistent if a scan is added while another is selected.
        this.applySelectionStyle();

        return group;
    }

    /** Shows or hides a scan's camera-path polyline. */
    setScanCameraPathVisible(scanId, visible) {
        const entry = this.scans.get(scanId);
        if (entry?.cameraPath) entry.cameraPath.visible = visible;
    }

    /**
     * Sets the camera-helper trim radius for a scan (0 = show everything). Only effective when the
     * scan was loaded with cullCenters; otherwise the geometry has no aMinDistance attribute and
     * the shader compare reads 0 → all points survive.
     */
    setScanTrimRadius(scanId, radius) {
        const entry = this.scans.get(scanId);
        if (!entry) return;
        setMaterialTrimRadius(entry.points.material, radius);
    }

    /** Sets the live decimation keep-fraction (0..1) for a scan (1 = keep all). */
    setScanDecimation(scanId, keepFraction) {
        const entry = this.scans.get(scanId);
        if (!entry) return;
        setMaterialDecimation(entry.points.material, keepFraction);
    }

    /**
     * Point counts for a scan at a given trim radius and decimation keep-fraction:
     * { total, visible, hidden, pct }. Decimation thins the trim survivors uniformly, so
     * visible ≈ trimSurvivors × keepFraction — i.e. what the baked PLY would contain. Falls back to
     * "all visible" for the trim part when the scan has no cull distances or isn't loaded.
     */
    getTrimCounts(scanId, radius, keepFraction = 1) {
        const entry = this.scans.get(scanId);
        const total = entry?.points?.geometry?.attributes?.position?.count ?? 0;
        const trimVisible = visibleAtRadius(entry?.trimDistribution, radius);
        const baseVisible = trimVisible == null ? total : trimVisible;
        const keep = Math.min(1, Math.max(0, keepFraction));
        const visible = Math.round(baseVisible * keep);
        const hidden = total - visible;
        return { total, visible, hidden, pct: total > 0 ? (hidden / total) * 100 : 0 };
    }

    /**
     * Largest dimension of a scan's local-space bounding box, useful for sizing the trim-radius
     * slider to the scan's actual extent. Returns 0 when the scan is not loaded.
     */
    getScanBoundsSize(scanId) {
        const entry = this.scans.get(scanId);
        if (!entry) return 0;

        const geometry = entry.points.geometry;
        if (!geometry) return 0;
        if (!geometry.boundingBox) geometry.computeBoundingBox();

        const size = new THREE.Vector3();
        geometry.boundingBox.getSize(size);
        return Math.max(size.x, size.y, size.z);
    }

    /**
     * Removes a scan from the scene and disposes its GPU resources.
     */
    removeScan(scanId) {
        const entry = this.scans.get(scanId);
        if (!entry) return;

        if (this.selectedScanId === scanId) this.deselectScan();

        this.scene.remove(entry.group);
        entry.points.geometry?.dispose();
        entry.points.material?.dispose();
        entry.cameraPath?.geometry?.dispose();
        entry.cameraPath?.material?.dispose();
        this.scans.delete(scanId);
    }

    /**
     * Applies a transform (API TransformViewModel shape) to a scan group. Missing parts default to
     * identity.
     */
    setScanTransform(scanId, transform) {
        const entry = this.scans.get(scanId);
        if (entry) this.applyTransform(entry.group, transform);
    }

    /**
     * Reads a scan group's current transform back in the API TransformViewModel shape, ready to
     * persist via PUT .../scans.
     */
    getScanTransform(scanId) {
        const entry = this.scans.get(scanId);
        if (!entry) return null;

        const g = entry.group;
        return {
            position: { x: g.position.x, y: g.position.y, z: g.position.z },
            rotation: { isEuler: true, x: g.rotation.x, y: g.rotation.y, z: g.rotation.z, order: g.rotation.order },
            scale: { x: g.scale.x, y: g.scale.y, z: g.scale.z },
        };
    }

    applyTransform(object, transform) {
        const position = transform?.position ?? {};
        const rotation = transform?.rotation ?? {};
        const scale = transform?.scale ?? {};

        object.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
        object.rotation.set(rotation.x ?? 0, rotation.y ?? 0, rotation.z ?? 0, rotation.order ?? 'XYZ');
        object.scale.set(scale.x ?? 1, scale.y ?? 1, scale.z ?? 1);
    }

    /**
     * Positions the camera from a saved viewpoint (the model version's ModelViewPoint position +
     * rotation). OrbitControls derives orientation from (position, target) and ignores
     * camera.rotation, so the saved rotation is converted into a target point — same approach as
     * the runtime ModelViewer. Returns false (so the caller can fall back to framing) when no
     * usable position is supplied.
     */
    applyCameraViewpoint(viewpoint) {
        const p = viewpoint?.position;
        const hasPosition = p && (p.x !== 0 || p.y !== 0 || p.z !== 0);
        if (!hasPosition) return false;

        this.camera.position.set(p.x, p.y, p.z);

        const r = viewpoint.rotation;
        const hasRotation = r && (r.x !== 0 || r.y !== 0 || r.z !== 0);
        if (hasRotation) {
            this.camera.rotation.set(r.x, r.y, r.z, 'XYZ');
            this.camera.updateMatrixWorld();

            const forward = new THREE.Vector3();
            this.camera.getWorldDirection(forward);

            const distance = this.orbitControls.target.distanceTo(this.camera.position) || 5;
            this.orbitControls.target.copy(this.camera.position).addScaledVector(forward, distance);
        }

        this.camera.updateProjectionMatrix();
        this.orbitControls.update();
        return true;
    }

    /**
     * The current camera pose in the shape UpdateModelVersionModel expects (position + euler
     * rotation), for saving as the version's default viewpoint.
     */
    getCameraPose() {
        return {
            positionX: this.camera.position.x,
            positionY: this.camera.position.y,
            positionZ: this.camera.position.z,
            rotationX: this.camera.rotation.x,
            rotationY: this.camera.rotation.y,
            rotationZ: this.camera.rotation.z,
        };
    }

    /**
     * Frames the camera so every loaded scan is in view.
     */
    frameAll() {
        if (!this.scans.size) return;

        const box = new THREE.Box3();
        for (const { group } of this.scans.values()) {
            box.expandByObject(group);
        }
        this.frameBox(box);
    }

    /**
     * Frames the camera on a single scan's bounds (used to frame the primary scan when no saved
     * viewpoint exists). Falls back to framing everything if the scan isn't loaded.
     */
    frameScan(scanId) {
        const entry = this.scans.get(scanId);
        if (!entry) {
            this.frameAll();
            return;
        }
        this.frameBox(new THREE.Box3().expandByObject(entry.group));
    }

    frameBox(box) {
        if (box.isEmpty()) return;

        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = this.camera.fov * (Math.PI / 180);
        const distance = Math.abs(maxDim / (2 * Math.tan(fov / 2))) * 1.5;

        this.camera.position.set(center.x + distance, center.y + distance, center.z + distance);
        this.camera.updateProjectionMatrix();

        if (this.orbitControls) {
            this.orbitControls.target.copy(center);
            this.orbitControls.maxDistance = Math.max(this.orbitControls.maxDistance, distance * 4);
            this.orbitControls.update();
        }
    }

    startAnimationLoop() {
        const animate = () => {
            this.animationFrameId = requestAnimationFrame(animate);
            this.orbitControls.update();
            this.animateCameraPaths();
            this.renderer.render(this.scene, this.camera);
        };
        animate();
    }

    // Flows the dashes along every scan's camera path to show the capture/walk direction.
    animateCameraPaths() {
        for (const entry of this.scans.values()) {
            const material = entry.cameraPath?.material;
            if (material) material.dashOffset -= 0.015;
        }
    }

    // Fat lines (LineMaterial) compute their pixel width from the viewport resolution, so it must be
    // (re)set whenever the canvas size changes — otherwise the line is the wrong thickness.
    sizeCameraPath(cameraPath) {
        if (!this.container || !cameraPath?.material?.resolution) return;
        cameraPath.material.resolution.set(this.container.clientWidth, this.container.clientHeight);
    }

    onResize() {
        if (!this.container || !this.renderer) return;
        const width = this.container.clientWidth;
        const height = this.container.clientHeight;
        // A collapsed/hidden container (0 in either dimension) makes aspect NaN/Infinity, which
        // blanks the canvas until reload. Skip — the observer fires again with real dimensions.
        if (width === 0 || height === 0) return;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);

        for (const entry of this.scans.values()) this.sizeCameraPath(entry.cameraPath);
    }

    /**
     * Tears down the engine: stops the loop, disposes scans and the renderer, removes the canvas.
     */
    dispose() {
        if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;

        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        if (this.renderer) {
            this.renderer.domElement.removeEventListener('pointerdown', this.handlePointerDown);
            this.renderer.domElement.removeEventListener('pointerup', this.handlePointerUp);
        }

        for (const scanId of [...this.scans.keys()]) {
            this.removeScan(scanId);
        }

        if (this.transformControls) {
            this.transformControls.detach();
            this.transformControls.dispose();
            this.transformControls = null;
        }

        this.orbitControls?.dispose();

        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.domElement?.remove();
        }

        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.orbitControls = null;
        this.container = null;
    }
}
